import { useEffect, useState } from 'react';
import { Icon } from '../design/icons';
import { storeLanguageChoice, storedLanguageChoice, t, type LanguageChoice } from '../i18n/i18n';
import { applyTheme, storedTheme, type ThemeChoice } from './theme';

/** The own-profile popover: appearance, language, sign out (the quiet destructive action last). */
export function ProfileMenu({ anchor, onClose, onSignOut }: { anchor: DOMRect; onClose(): void; onSignOut(): void }) {
  const [theme, setTheme] = useState<ThemeChoice>(storedTheme());
  const [lang, setLang] = useState<LanguageChoice>(storedLanguageChoice());

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.profile-popover')) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  const pickTheme = (c: ThemeChoice) => {
    setTheme(c);
    applyTheme(c, true);
  };
  const pickLang = (c: LanguageChoice) => {
    setLang(c);
    storeLanguageChoice(c);
    // Strings are resolved at render time; a reload is the honest way to re-render everything,
    // and the local-first boot makes it instant.
    location.reload();
  };

  return (
    <div className="popover profile-popover" style={{ position: 'fixed', left: anchor.left, bottom: window.innerHeight - anchor.top + 8, width: 260 }} role="menu">
      <div className="menu-label">{t('web.menu.appearance')}</div>
      {(['system', 'light', 'dark'] as ThemeChoice[]).map((c) => (
        <button key={c} className="menu-item" role="menuitemradio" aria-checked={theme === c} onClick={() => pickTheme(c)}>
          <Icon name={c === 'dark' ? 'moon' : c === 'light' ? 'sun' : 'sidebar'} size={16} />
          {t(`web.theme.${c}`)}
          {theme === c ? <Icon name="check" size={14} style={{ marginLeft: 'auto' }} /> : null}
        </button>
      ))}
      <div className="menu-sep" />
      <div className="menu-label">{t('profile.language.section')}</div>
      {(['system', 'es', 'en'] as LanguageChoice[]).map((c) => (
        <button key={c} className="menu-item" role="menuitemradio" aria-checked={lang === c} onClick={() => pickLang(c)}>
          <Icon name="globe" size={16} />
          {c === 'system' ? t('profile.language.system') : c === 'es' ? 'Español' : 'English'}
          {lang === c ? <Icon name="check" size={14} style={{ marginLeft: 'auto' }} /> : null}
        </button>
      ))}
      <div className="menu-sep" />
      <button className="menu-item danger" onClick={onSignOut}>
        <Icon name="signOut" size={16} />
        {t('profile.signOut')}
      </button>
    </div>
  );
}
